import type { Message, Timestamp } from './types';

/** 연속 그룹핑 임계값 (PLAN 6-4장 — 동일 발신자 3초 내) */
export const GROUP_WINDOW_MS = 3_000;

export interface GroupedMessage {
  message: Message;
  /** 그룹의 첫 메시지 — 앞뒤 여백을 붙인다 */
  isGroupStart: boolean;
  /** 그룹의 마지막 메시지 — 타임스탬프/틱을 붙인다 */
  isGroupEnd: boolean;
  showSender: boolean;
}

interface Groupable {
  id: string;
  senderId: string;
  createdAt?: Timestamp | null;
  clientCreatedAt?: number;
}

/**
 * Timestamp 또는 밀리초에서 안전하게 ms 숫자를 추출한다.
 * Firestore 낙관적 업데이트 중 createdAt이 null이거나 pending인 경우를 방어한다.
 */
export function timestampToMs(val: unknown, fallbackMs: number = 0): number {
  if (!val) return fallbackMs;
  if (typeof (val as { toDate?: () => Date }).toDate === 'function') {
    try {
      return (val as { toDate: () => Date }).toDate().getTime();
    } catch {
      return fallbackMs;
    }
  }
  if (typeof val === 'number') return val;
  if (val instanceof Date) return val.getTime();
  return fallbackMs;
}

/**
 * 연속된 동일 발신자 메시지를 그룹화한다.
 * 시각은 Date로 변환해 비교한다 (Firestore Timestamp 의존 제거).
 */
export function groupMessages(messages: Groupable[]): GroupedMessage[] {
  if (messages.length === 0) return [];

  const result: GroupedMessage[] = [];
  for (let i = 0; i < messages.length; i += 1) {
    const current = messages[i];
    const prev = messages[i - 1];
    const next = messages[i + 1];
    if (!current) continue;

    const prevMs = prev ? timestampToMs(prev.createdAt, prev.clientCreatedAt ?? 0) : 0;
    const curMs = timestampToMs(current.createdAt, current.clientCreatedAt ?? 0);
    const nextMs = next ? timestampToMs(next.createdAt, next.clientCreatedAt ?? 0) : 0;

    const continuesPrev =
      prev !== undefined && prev.senderId === current.senderId && curMs - prevMs < GROUP_WINDOW_MS;
    const continuesNext =
      next !== undefined && next.senderId === current.senderId && nextMs - curMs < GROUP_WINDOW_MS;

    result.push({
      message: current as Message,
      isGroupStart: !continuesPrev,
      isGroupEnd: !continuesNext,
      // 그룹 첫 메시지에만 발신자 이름/아바타 표시
      showSender: !continuesPrev,
    });
  }
  return result;
}

// ── 리액션 (PLAN 5-4장) ───────────────────────────────────────────────

/** 리액션 칩에 표시할 최대 인원 (초과 시 +N) */
export const REACTION_DISPLAY_LIMIT = 12;

/** 기본 팔레트 — 상단 6개 (PLAN 5-4장, WhatsApp 계열) */
export const REACTION_PALETTE = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;

/** 확장 팔레트 24개 — "더 보기" 그리드 */
export const REACTION_PALETTE_EXTENDED = [
  '🔥',
  '🎉',
  '✅',
  '👀',
  '🤣',
  '💯',
  '🙌',
  '😍',
  '😊',
  '😎',
  '🤔',
  '😅',
  '😭',
  '😡',
  '🥰',
  '🤗',
  '👏',
  '💪',
  '✨',
  '⭐',
  '🍀',
  '🎂',
  '☕',
  '🍻',
] as const;

/** 기본 + 확장 전체 (중복 제거) — 피커 그리드와 기본 리액션 설정에서 쓴다 */
export const REACTION_ALL: string[] = [
  ...new Set<string>([...REACTION_PALETTE, ...REACTION_PALETTE_EXTENDED]),
];

/**
 * 버블 하단 칩 (PLAN 5-4장).
 * 표시 순서는 삽입 순서를 그대로 따른다 — 카운트가 바뀌어도 칩이 튀지 않는다.
 */
export interface ReactionChip {
  emoji: string;
  count: number;
  /** 리액션한 사람 uid 목록 (아바타 스택용) */
  uids: string[];
  /** 내가 누른 이모지인가 (칩 강조) */
  mine: boolean;
}

export function reactionChips(reactions: Record<string, string[]>, uid: string): ReactionChip[] {
  return Object.entries(reactions)
    .filter(([, uids]) => uids.length > 0)
    .map(([emoji, uids]) => ({
      emoji,
      count: uids.length,
      uids,
      mine: uids.includes(uid),
    }));
}

/** 이 이모지에 리액션한 사람들 (칩 탭 → 아바타 스택) */
export function reactorsOf(reactions: Record<string, string[]>, emoji: string): string[] {
  return reactions[emoji] ?? [];
}

/**
 * 사용자 리액션 토글:
 * 같은 이모지를 이미 누른 상태면 해제(제거), 안 누른 상태면 추가.
 * 메세지당 복수개의 서로 다른 이모지 리액션을 허용한다.
 */
export function resolveReactionUpdate(
  reactions: Record<string, string[]>,
  emoji: string,
  uid: string,
): Record<string, string[]> {
  const isMine = reactions[emoji]?.includes(uid) ?? false;
  if (isMine) {
    const nextUids = (reactions[emoji] ?? []).filter((u) => u !== uid);
    if (nextUids.length === 0) {
      const { [emoji]: _removed, ...rest } = reactions;
      return rest;
    }
    return { ...reactions, [emoji]: nextUids };
  }
  return { ...reactions, [emoji]: [...(reactions[emoji] ?? []), uid] };
}

function cleaned(map: Record<string, string[]>): Record<string, string[]> {
  return Object.fromEntries(Object.entries(map).filter(([, uids]) => uids.length > 0));
}

/**
 * 칩의 "나만 취소" — 이 이모지에서 내 것만 제거한다.
 * 내가 누른 게 아니면 원본을 그대로 돌려준다(불필요한 쓰기를 만들지 않는다).
 */
export function removeMyReaction(
  reactions: Record<string, string[]>,
  emoji: string,
  uid: string,
): Record<string, string[]> {
  const uids = reactions[emoji];
  if (!uids || !uids.includes(uid)) return reactions;
  return cleaned({ ...reactions, [emoji]: uids.filter((u) => u !== uid) });
}

/** 칩 롱프레스의 "모두 취소" — 이모지 키를 통째로 지우다 (발신자/관리자 전용) */
export function clearReaction(
  reactions: Record<string, string[]>,
  emoji: string,
): Record<string, string[]> {
  if (reactions[emoji] === undefined) return reactions;
  const { [emoji]: _removed, ...rest } = reactions;
  return rest;
}

export function countReactions(reactions: Record<string, string[]>): number {
  return Object.values(reactions).reduce((sum, uids) => sum + uids.length, 0);
}

/** 특정 사용자의 단일 리액션 (하위 호환) */
export function myReaction(reactions: Record<string, string[]>, uid: string): string | undefined {
  return Object.keys(reactions).find((e) => reactions[e]?.includes(uid));
}

/** 특정 사용자의 모든 리액션 이모지 목록 (다중 리액션 지원) */
export function myReactions(reactions: Record<string, string[]>, uid: string): string[] {
  return Object.keys(reactions).filter((e) => reactions[e]?.includes(uid));
}

// ── 전송 상태 (3단계 틱, PLAN 5-4장) ───────────────────────────────────

export type DeliveryState = 'sending' | 'sent' | 'delivered' | 'read' | 'failed';

export interface DeliveryInput {
  /** Firestore snapshot.metadata.hasPendingWrites */
  hasPendingWrites: boolean;
  failed?: boolean;
  createdAt?: Timestamp | null;
  clientCreatedAt?: number;
  /** 상대방(또는 방)의 lastReadAt */
  peerLastReadAt?: Timestamp | null;
  /** 읽음 표시를 받는 사람이 꺼둔 경우 delivered까지만 */
  peerAcceptsReadReceipts?: boolean;
}

export function deliveryState(input: DeliveryInput): DeliveryState {
  if (input.failed) return 'failed';
  if (input.hasPendingWrites) return 'sending';
  if (!input.peerLastReadAt) return 'sent';

  const created = timestampToMs(input.createdAt, input.clientCreatedAt ?? 0);
  const peerRead = timestampToMs(input.peerLastReadAt, 0);
  if (peerRead < created) return 'sent';
  if (input.peerAcceptsReadReceipts === false) return 'delivered';
  return 'read';
}

// ── 미읽음 (PLAN 5-8장) ───────────────────────────────────────────────

/** 읽지 않은 메시지 수 = 내 lastReadAt 이후, 내가 아닌 사람의 메시지 */
export function computeUnread(
  messages: { senderId: string; createdAt?: Timestamp | null; clientCreatedAt?: number }[],
  myUid: string,
  lastReadAt?: Timestamp | null,
): number {
  const cutoff = timestampToMs(lastReadAt, 0);
  return messages.filter(
    (m) => m.senderId !== myUid && timestampToMs(m.createdAt, m.clientCreatedAt ?? 0) > cutoff,
  ).length;
}

// ── 사라지는 메시지 (PLAN 0-B 변경 2) ──────────────────────────────────

/** 클라이언트 가드: 만료된 메시지는 렌더에서 제외 */
export function isExpired(
  message: { expiresAt?: Timestamp | null },
  now: number = Date.now(),
): boolean {
  return (
    message.expiresAt !== undefined &&
    message.expiresAt !== null &&
    timestampToMs(message.expiresAt, 0) <= now
  );
}

/** 메시지 만료 시간 라벨 키 (PLAN 0-B 변경 2) */
export function ttlLabelKey(ttl: number): string {
  switch (ttl) {
    case 86400:
      return 'group.on24h';
    case 604800:
      return 'group.on7d';
    case 7776000:
      return 'group.on90d';
    default:
      return 'group.off';
  }
}

/**
 * 대화방 내 표시 이름(대화명) 우선순위 해결 (PLAN 0.3):
 * 1. Admin이 부여한 별칭 (adminAlias)
 * 2. 사용자 본인이 설정한 대화방 내 닉네임 (memberNickname)
 * 3. 사용자 프로필 표시 이름 (profileDisplayName)
 * 4. 핸들 (@handle)
 */
export function resolveDisplayName({
  adminAlias,
  memberNickname,
  profileDisplayName,
  handle,
}: {
  adminAlias?: string | null;
  memberNickname?: string | null;
  profileDisplayName?: string | null;
  handle?: string | null;
}): string {
  if (adminAlias?.trim()) return adminAlias.trim();
  if (memberNickname?.trim()) return memberNickname.trim();
  if (profileDisplayName?.trim()) return profileDisplayName.trim();
  if (handle?.trim()) return `@${handle.trim().replace(/^@/, '')}`;
  return '';
}

/**
 * YYMMDD/HH:mm 형식으로 시각을 포맷한다.
 * e.g., 261005/14:30
 */
export function formatDateTimeShort(val: unknown, fallbackMs: number = 0): string {
  const ms = timestampToMs(val, fallbackMs);
  if (!ms || Number.isNaN(ms)) return '';
  const date = new Date(ms);
  const yy = String(date.getFullYear() % 100).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${yy}${mm}${dd}/${hh}:${min}`;
}
